/**
 * A phone asked to be a service device.
 *
 * Above every view, like the alarm, because the person holding the phone is waiting on it —
 * often standing at the office counter. Only an administrator sees it: the list is theirs to
 * read, and a dispatcher asking for it is refused, which is the same as there being nothing to
 * show. The hub says when to look again; it carries nothing, so the requests never reach a
 * desk that may not answer them.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatcherHub } from '../realtime/useDispatcherHub';
import { useTenantFormats } from '../shell/formats';
import { Banner, Button } from '../ui';
import { ApproveDeviceDialog } from './ApproveDeviceDialog';
import { devicesApi, type DeviceRequest } from './api';

export function DeviceRequestBanner() {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const [requests, setRequests] = useState<DeviceRequest[]>([]);
  const [open, setOpen] = useState<DeviceRequest | null>(null);

  const refresh = useCallback(async () => {
    try {
      setRequests(await devicesApi.requests());
    } catch {
      // Not an administrator, most often. Nothing to show and nothing to say.
      setRequests([]);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void refresh();
  }, [refresh]);

  useDispatcherHub({
    onServiceDeviceRequestsChanged: () => void refresh(),
    onReconnected: () => void refresh(),
  });

  return (
    <>
      {requests.length > 0 && (
        <div className="flex flex-col gap-1 px-3 pt-3">
          {requests.map((request) => (
            <Banner
              key={request.id}
              tone="warn"
              actions={
                <Button size="sm" variant="primary" onClick={() => setOpen(request)}>
                  {t('devices.banner.open')}
                </Button>
              }
            >
              <strong className="font-semibold">
                {t('devices.banner.asked', { code: request.code })}
              </strong>
              {' · '}
              {request.deviceName ?? t('devices.unnamed')}
              {' · '}
              {formats.time(request.createdAt)}
            </Banner>
          ))}
        </div>
      )}

      {open !== null && (
        <ApproveDeviceDialog
          request={open}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            void refresh();
          }}
        />
      )}
    </>
  );
}
